import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Brain, Sparkles, RefreshCw, Save, Send, AlertTriangle,
  Activity, GitBranch, TrendingUp, Layers, ListOrdered, Waves, CheckCircle2, Leaf,
} from 'lucide-react';

/**
 * DECODE Workspace (v2 — backbone-driven)
 *
 * Architecture:
 *   Left column  — Intelligence layer (backbone-driven, read-only)
 *                  confidence · root drivers · amplifiers · axis map ·
 *                  contradictions · priorities · trajectory
 *   Right column — Refinement layer (admin-editable)
 *                  seeded from backbone; overrides persist as DECODE fields
 *
 * All interpretation logic lives in the ReGenflō Intelligence Engine.
 * This page is a workspace, not a reasoner.
 */
export default function DecodeWorkspace() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const presetSub = params.get('subscriber');

  const [subscribers, setSubscribers] = useState([]);
  const [selectedSub, setSelectedSub] = useState(presetSub || '');
  const [intelligence, setIntelligence] = useState(null); // full engine output
  const [runId, setRunId] = useState(null);
  const [draft, setDraft] = useState(null); // editable DECODE fields
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [existingDraftId, setExistingDraftId] = useState(null);
  const [artifactIssues, setArtifactIssues] = useState(null); // null | [{field, matches}]

  useEffect(() => {
    axios.get(`${API}/users?role=subscriber`).then(r => setSubscribers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedSub) loadWorkspace();
    // eslint-disable-next-line
  }, [selectedSub]);

  const loadWorkspace = async () => {
    setLoading(true);
    setIntelligence(null);
    setDraft(null);
    setRunId(null);
    setExistingDraftId(null);
    try {
      // 1. Check for an existing draft first
      const draftResp = await axios.get(`${API}/decodes/${selectedSub}/draft`).catch(() => ({ data: { draft: null } }));
      if (draftResp.data.draft) {
        setDraft(mapDraftToForm(draftResp.data.draft));
        setExistingDraftId(draftResp.data.draft.id);
        // reuse the detailed_data intelligence if present
        if (draftResp.data.draft.detailed_data?.axis_analysis) {
          setIntelligence({
            axis_analysis: draftResp.data.draft.detailed_data.axis_analysis,
            priorities: draftResp.data.draft.detailed_data.priorities || [],
            contradictions: draftResp.data.draft.detailed_data.contradictions || [],
            relationship_signals: draftResp.data.draft.detailed_data.relationship_signals || [],
            trajectory: draftResp.data.draft.detailed_data.trajectory || {},
            module_outputs: draftResp.data.draft.detailed_data.module_outputs || {},
            meta: {
              engine_version: draftResp.data.draft.detailed_data.engine_version,
              overall_confidence: draftResp.data.draft.detailed_data.overall_confidence,
              twinflo_integrated: draftResp.data.draft.detailed_data.twinflo_integrated,
              execution_integrated: draftResp.data.draft.detailed_data.execution_integrated,
              wearable_integrated: draftResp.data.draft.detailed_data.wearable_integrated,
              sleep_integrated: draftResp.data.draft.detailed_data.sleep_integrated,
              nutrition_integrated: draftResp.data.draft.detailed_data.nutrition_integrated,
            },
            input_summary: draftResp.data.draft.detailed_data.intelligence_input_summary || {},
            narrative: {
              clinical_summary: draftResp.data.draft.internal_technical_summary,
              regenflo_summary: draftResp.data.draft.strategic_direction,
              subscriber_friendly_summary: draftResp.data.draft.subscriber_facing_summary,
            },
          });
          setLoading(false);
          return;
        }
      }
      // 2. Otherwise compute fresh
      await runBackbone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load workspace');
    } finally {
      setLoading(false);
    }
  };

  const runBackbone = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/decodes/draft-from-backbone/${selectedSub}`);
      setIntelligence(r.data.intelligence_output);
      setRunId(r.data.intelligence_run_id);
      setDraft(mapDraftToForm(r.data.draft));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Backbone computation failed');
      setIntelligence(null);
    } finally {
      setLoading(false);
    }
  };

  const recomputeFresh = async () => {
    if (!confirm('Recompute overwrites the current draft refinement fields with new backbone output. Continue?')) return;
    await runBackbone();
    toast.success('Re-seeded from latest backbone analysis');
  };

  const updateField = (field, value) => setDraft(d => ({ ...d, [field]: value }));

  const saveDraft = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const body = toApiPayload(draft, intelligence, runId);
      const r = await axios.post(`${API}/decodes/draft/${selectedSub}`, body);
      setExistingDraftId(r.data.decode_id);
      toast.success('Draft saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const publishDraft = async (skipArtifactCheck = false) => {
    if (!existingDraftId) {
      toast.error('Save the draft first');
      return;
    }
    // Pre-publish quality gate — deterministic scan for engine artifacts in subscriber-facing fields
    if (!skipArtifactCheck) {
      const issues = scanForEngineArtifacts(draft);
      if (issues.length > 0) {
        setArtifactIssues(issues);
        return; // show warning dialog; admin picks an action
      }
    }
    if (!confirm('Publish this DECODE to the subscriber? This sends an email and archives any prior published DECODE.')) return;
    setArtifactIssues(null);
    setPublishing(true);
    try {
      await axios.post(`${API}/decodes/draft/${selectedSub}/publish`);
      toast.success('DECODE published to subscriber');
      navigate('/admin/subscribers');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Publish failed');
    } finally { setPublishing(false); }
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-[1600px] mx-auto" data-testid="decode-workspace">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-light text-white flex items-center gap-2">
              <Brain className="w-6 h-6 text-[#C89B7B]" />
              DECODE Workspace
            </h1>
            <p className="text-[11px] text-white/40 mt-1 tracking-wide">
              Backbone-driven interpretation · admin refinement · subscriber-safe publish
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedSub} onValueChange={setSelectedSub}>
              <SelectTrigger className="w-[280px] h-9 bg-white/[0.02] border-white/10 text-white text-xs" data-testid="decode-subscriber-select">
                <SelectValue placeholder="Select subscriber" />
              </SelectTrigger>
              <SelectContent>
                {subscribers.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name || s.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedSub && (
              <Button
                size="sm" variant="outline" disabled={loading}
                onClick={recomputeFresh}
                className="h-9 px-3 text-[11px] border-white/10 text-white/70"
                data-testid="decode-recompute-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                Recompute
              </Button>
            )}
          </div>
        </div>

        {!selectedSub && <EmptyState msg="Select a subscriber to open the DECODE workspace." />}
        {selectedSub && loading && <EmptyState msg="Running the intelligence backbone…" />}
        {selectedSub && !loading && !intelligence && (
          <EmptyState msg="This subscriber has no validated lab reports yet. Validate labs first." />
        )}

        {intelligence && draft && (
          <div className="grid grid-cols-1 xl:grid-cols-[1.1fr_1fr] gap-6">
            <IntelligencePane intelligence={intelligence} />
            <RefinementPane
              draft={draft}
              updateField={updateField}
              saving={saving}
              publishing={publishing}
              saveDraft={saveDraft}
              publishDraft={publishDraft}
              hasDraftSaved={!!existingDraftId}
            />
          </div>
        )}

        {/* Pre-publish quality gate — warning (not block) */}
        {artifactIssues && (
          <ArtifactWarningDialog
            issues={artifactIssues}
            onReview={() => setArtifactIssues(null)}
            onPublishAnyway={() => { setArtifactIssues(null); publishDraft(true); }}
            onCancel={() => setArtifactIssues(null)}
          />
        )}
      </div>
    </AdminLayout>
  );
}

// ──────────────────────── INTELLIGENCE PANE (left) ────────────────────────

function IntelligencePane({ intelligence }) {
  const meta = intelligence.meta || {};
  const cf = meta.overall_confidence || {};
  const axes = intelligence.axis_analysis || [];
  const priorities = intelligence.priorities || [];
  const roots = priorities.filter(p => p.type === 'root_driver');
  const amplifiers = priorities.filter(p => p.type === 'amplifier');
  const contradictions = intelligence.contradictions || [];
  const signals = intelligence.relationship_signals || [];
  const trajectory = intelligence.trajectory || {};
  const flaggedAxes = axes.filter(a => a.status !== 'optimal' && a.status !== 'insufficient_data');

  return (
    <div className="space-y-4" data-testid="decode-intelligence-pane">

      <Panel title="Core Intelligence Summary" icon={Sparkles}>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] text-white/50">
          <span>Engine <span className="text-white/80">{meta.engine_version}</span></span>
          <span>Mode <span className="text-white/80">decode</span></span>
          <span>Confidence <span className="text-white/80">{cf.label} ({cf.score})</span></span>
          <span>Reports <span className="text-white/80">{intelligence.input_summary?.lab_report_count}</span></span>
          <span>Markers <span className="text-white/80">{intelligence.input_summary?.marker_count}</span></span>
          <span>Condition <span className="text-white/80">{intelligence.input_summary?.condition || '—'}</span></span>
        </div>
        {(intelligence.narrative?.regenflo_summary) && (
          <p className="text-xs text-[#C89B7B]/85 leading-relaxed italic mt-3" data-testid="decode-regenflo-summary">
            {intelligence.narrative.regenflo_summary}
          </p>
        )}
      </Panel>

      <TwinfloInfluencePanel intelligence={intelligence} />

      <ExecutionInfluencePanel intelligence={intelligence} />

      <PassiveSignalsPanel intelligence={intelligence} />

      <BiocontextMemoryInfluencePanel intelligence={intelligence} />

      <Panel title="Root Drivers" icon={Activity} testid="decode-root-drivers">
        {roots.length ? roots.map(p => (
          <PriorityRow key={p.rank} p={p} tone="red" />
        )) : <EmptyLine text="No root drivers detected at current confidence." />}
      </Panel>

      <Panel title="Amplifiers" icon={GitBranch} testid="decode-amplifiers">
        {amplifiers.length ? amplifiers.map(p => (
          <PriorityRow key={p.rank} p={p} tone="amber" />
        )) : <EmptyLine text="No amplifiers identified." />}
      </Panel>

      <Panel title="Axis Map" icon={Layers} testid="decode-axis-map">
        {flaggedAxes.length ? flaggedAxes.slice(0, 12).map(a => (
          <div key={a.axis_key} className="grid grid-cols-[1fr_auto_auto] items-center gap-3 py-1.5 border-b border-white/5 last:border-0">
            <div className="flex items-center gap-2">
              <StatusDot status={a.status} />
              <span className="text-xs text-white/80">{a.axis_name}</span>
            </div>
            <span className="text-[10px] text-white/40 tabular-nums">{a.score.toFixed(2)}</span>
            <Badge className="text-[9px] bg-white/5 text-white/60 rounded-sm">{a.status}</Badge>
          </div>
        )) : <EmptyLine text="All axes within optimal range or insufficient data." />}
      </Panel>

      <Panel title="Contradictions / Hidden Risk" icon={AlertTriangle} testid="decode-contradictions">
        {contradictions.length ? contradictions.map(c => (
          <div key={c.key} className="border-l-2 border-red-400/30 pl-3 py-1.5">
            <p className="text-xs text-white/85">{c.label}</p>
            <p className="text-[11px] text-white/45 mt-0.5 leading-relaxed">{c.summary}</p>
          </div>
        )) : <EmptyLine text="No contradictions detected." />}
      </Panel>

      {signals.length > 0 && (
        <Panel title="Relationship Signals" icon={GitBranch} testid="decode-signals">
          {signals.map(s => (
            <div key={s.signal_key} className="border-l-2 border-white/10 pl-3 py-1.5">
              <p className="text-xs text-white/80">{s.signal_name}</p>
              <p className="text-[11px] text-white/45 mt-0.5 leading-relaxed">{s.summary}</p>
            </div>
          ))}
        </Panel>
      )}

      <Panel title="Priorities" icon={ListOrdered} testid="decode-priorities">
        {priorities.slice(0, 8).map(p => (
          <PriorityRow key={p.rank} p={p} />
        ))}
      </Panel>

      <Panel title="Trajectory" icon={TrendingUp} testid="decode-trajectory">
        <div className="flex items-center gap-3">
          <Badge className="text-[10px] bg-white/5 text-white/75 rounded-sm">{trajectory.status}</Badge>
          <span className="text-[11px] text-white/50">{trajectory.summary}</span>
        </div>
      </Panel>
    </div>
  );
}

// ──────────────────────── REFINEMENT PANE (right) ────────────────────────

function RefinementPane({ draft, updateField, saving, publishing, saveDraft, publishDraft, hasDraftSaved }) {
  return (
    <div className="space-y-4 xl:sticky xl:top-4 xl:self-start" data-testid="decode-refinement-pane">
      <Panel title="Admin Refinement" icon={Sparkles}>
        <p className="text-[11px] text-white/40 leading-relaxed">
          Fields are seeded from the backbone. Refine freely — your edits persist on save and are what the
          subscriber sees on publish. Arrays (drivers, objectives) accept one item per line.
        </p>
      </Panel>

      <EditableField
        label="Strategic Direction"
        value={draft.strategic_direction}
        onChange={v => updateField('strategic_direction', v)}
        rows={4}
        testid="decode-field-strategic"
      />

      <EditableField
        label="Current State"
        value={draft.current_state}
        onChange={v => updateField('current_state', v)}
        rows={2}
        testid="decode-field-state"
      />

      <EditableField
        label="Current Modifiable Window"
        value={draft.current_modifiable_window}
        onChange={v => updateField('current_modifiable_window', v)}
        rows={2}
        testid="decode-field-window"
      />

      <EditableListField
        label="Top Drivers (one per line)"
        value={draft.top_drivers}
        onChange={v => updateField('top_drivers', v)}
        rows={4}
        testid="decode-field-drivers"
      />

      <EditableListField
        label="Phase Objectives (one per line)"
        value={draft.phase_objectives}
        onChange={v => updateField('phase_objectives', v)}
        rows={4}
        testid="decode-field-objectives"
      />

      <EditableField
        label="BAS Summary (biological)"
        value={draft.bas_summary}
        onChange={v => updateField('bas_summary', v)}
        rows={3}
      />
      <EditableField
        label="NAS Summary (neurological)"
        value={draft.nas_summary}
        onChange={v => updateField('nas_summary', v)}
        rows={3}
      />
      <EditableField
        label="TAS Summary (terrain / toxic)"
        value={draft.tas_summary}
        onChange={v => updateField('tas_summary', v)}
        rows={3}
      />

      <EditableField
        label="Subscriber-Facing Summary"
        value={draft.subscriber_facing_summary}
        onChange={v => updateField('subscriber_facing_summary', v)}
        rows={5}
        testid="decode-field-subscriber"
      />

      <EditableField
        label="Internal Technical Notes"
        value={draft.internal_technical_summary}
        onChange={v => updateField('internal_technical_summary', v)}
        rows={4}
        testid="decode-field-internal"
      />

      <div className="flex items-center gap-3 sticky bottom-0 bg-[#0b0e14]/90 backdrop-blur py-4 -mx-1 px-1 border-t border-white/10">
        <Button
          disabled={saving}
          onClick={saveDraft}
          className="bg-white/10 hover:bg-white/15 text-white border border-white/15 h-10 text-xs"
          data-testid="decode-save-draft-btn"
        >
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save Draft'}
        </Button>
        <Button
          disabled={publishing || !hasDraftSaved}
          onClick={() => publishDraft()}
          className="bg-[#C89B7B] hover:bg-[#B88A6A] text-[#080C14] h-10 text-xs"
          data-testid="decode-publish-btn"
        >
          <Send className="w-4 h-4 mr-2" /> {publishing ? 'Publishing…' : 'Publish to Subscriber'}
        </Button>
        {!hasDraftSaved && (
          <span className="text-[10px] text-white/40">Save draft first to enable publish.</span>
        )}
      </div>
    </div>
  );
}

// ──────────────────────── primitives ────────────────────────

const Panel = ({ title, icon: Icon, children, testid }) => (
  <Card className="bg-white/[0.02] border-white/[0.06]" data-testid={testid}>
    <CardContent className="p-4 space-y-2">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] text-white/40">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {title}
      </div>
      {children}
    </CardContent>
  </Card>
);

// ──────────────────────── TwinFlō Influence Panel ────────────────────────
// Read-only visibility layer showing how live TwinFlō state influenced this
// DECODE run. Reads from intelligence.module_outputs.twinflo and
// intelligence.module_outputs.decode.twinflo_influence.

function TwinfloInfluencePanel({ intelligence }) {
  const tw = intelligence?.module_outputs?.twinflo || {};
  const infl = intelligence?.module_outputs?.decode?.twinflo_influence || {};
  const integrated = !!intelligence?.meta?.twinflo_integrated;

  // 3 visual states: absent, stale, fresh/aged integrated
  if (!integrated && !tw.present && !tw.is_stale) {
    return (
      <Card
        className="bg-white/[0.02] border-dashed border-white/10"
        data-testid="decode-twinflo-panel-absent"
      >
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] text-white/35">
            <Waves className="w-3.5 h-3.5" /> TwinFlō Influence
          </div>
          <p className="text-[11px] text-white/40 italic mt-2">
            No TwinFlō signal available for this subscriber — DECODE is using lab + context only.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (tw.is_stale) {
    return (
      <Card
        className="bg-amber-950/10 border-amber-400/20"
        data-testid="decode-twinflo-panel-stale"
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] text-amber-200/70">
              <Waves className="w-3.5 h-3.5" /> TwinFlō Influence
            </div>
            <Badge className="text-[9px] bg-amber-400/15 text-amber-200 rounded-sm border-0">stale</Badge>
          </div>
          <p className="text-[11px] text-amber-200/70 mt-2 leading-relaxed">
            TwinFlō snapshot is {tw.days_since ?? '>14'} days old — excluded from weighting.
            Lab + context drive this DECODE alone.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Present + integrated
  const domLabel = labelForState(tw.dominant_state);
  const secLabel = labelForState(tw.secondary_state);
  const pat = tw.state_pattern_7d || {};
  const stressedDays = (pat.stressed || 0) + (pat.triggered || 0);
  const stableDays = pat.stable || 0;
  const freshBadge = tw.is_fresh
    ? { label: 'fresh', cls: 'bg-emerald-400/15 text-emerald-200' }
    : { label: `${tw.days_since ?? '—'}d old`, cls: 'bg-white/10 text-white/60' };
  const categoryCls =
    tw.category === 'optimal' ? 'bg-emerald-400/15 text-emerald-200'
      : tw.category === 'depletion' ? 'bg-blue-400/15 text-blue-200'
        : 'bg-rose-400/15 text-rose-200';

  const influencedAxes = tw.influenced_axes || [];
  const tPriorities = tw.twinflo_priorities || infl.priorities_added || [];
  const tContradictions = tw.twinflo_contradictions || [];
  const trajectoryAdjusted = !!infl.trajectory_adjusted;

  return (
    <Card
      className="border-[1px] border-[#C89B7B]/25 bg-gradient-to-br from-[#C89B7B]/[0.05] to-white/[0.02]"
      data-testid="decode-twinflo-panel"
    >
      <CardContent className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] text-[#C89B7B]/90">
            <Waves className="w-3.5 h-3.5" /> TwinFlō Influence
          </div>
          <div className="flex items-center gap-1.5">
            <Badge className={`text-[9px] rounded-sm border-0 ${freshBadge.cls}`}>{freshBadge.label}</Badge>
            {tw.category && (
              <Badge className={`text-[9px] rounded-sm border-0 ${categoryCls}`}>{tw.category}</Badge>
            )}
          </div>
        </div>

        <p className="text-[11px] text-white/55 leading-relaxed italic">
          Live neurological/behavioral state signal — <span className="text-white/70">contextualizes</span> biological interpretation, never replaces it.
        </p>

        {/* State summary strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-1">
          <StatTile
            label="Dominant state"
            value={domLabel}
            sub={secLabel ? `+ ${secLabel}` : null}
            testid="twinflo-dominant"
          />
          <StatTile
            label="Intensity"
            value={tw.intensity != null ? Number(tw.intensity).toFixed(2) : '—'}
            sub={tw.risk_projection != null ? `risk ${Number(tw.risk_projection).toFixed(2)}` : null}
            testid="twinflo-intensity"
          />
          <StatTile
            label="7-day pattern"
            value={`${stressedDays} strained / ${stableDays} stable`}
            sub={tw.entries_7d ? `${tw.entries_7d} entries` : 'no entries'}
            testid="twinflo-pattern"
          />
          <StatTile
            label="Snapshot"
            value={tw.snapshot_date || '—'}
            sub={tw.is_fresh ? 'fresh' : `${tw.days_since ?? '—'}d ago`}
            testid="twinflo-snapshot"
          />
        </div>

        {/* Axes nudged */}
        <div className="pt-2">
          <p className="text-[10px] uppercase tracking-[0.1em] text-white/40 mb-1.5">Axes nudged by TwinFlō</p>
          {influencedAxes.length ? (
            <div className="flex flex-wrap gap-1.5" data-testid="twinflo-influenced-axes">
              {influencedAxes.slice(0, 6).map(a => (
                <Badge
                  key={a.axis_key}
                  className="text-[10px] bg-[#C89B7B]/10 text-[#C89B7B] border border-[#C89B7B]/20 rounded-sm"
                  title={a.axis_name}
                >
                  {a.axis_name} × {Number(a.twinflo_multiplier || 1).toFixed(2)}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-white/30 italic">No axes nudged at current TwinFlō intensity.</p>
          )}
        </div>

        {/* Priorities elevated */}
        <div>
          <p className="text-[10px] uppercase tracking-[0.1em] text-white/40 mb-1.5">Priorities from TwinFlō</p>
          {tPriorities.length ? (
            <div className="space-y-1" data-testid="twinflo-priorities">
              {tPriorities.map((p, i) => (
                <div key={`${p.source_key || p}-${i}`} className="border-l-2 border-[#C89B7B]/40 pl-3 py-0.5">
                  <div className="flex items-center gap-2 text-xs">
                    <Badge className="text-[8px] rounded-sm bg-[#C89B7B]/20 text-[#C89B7B] border-0">
                      {p.type || 'signal'}
                    </Badge>
                    <span className="text-white/80">{typeof p === 'string' ? p : p.label}</span>
                    {p.severity && <span className="text-white/30 ml-auto text-[10px]">{p.severity}</span>}
                  </div>
                  {p.summary && (
                    <p className="text-[11px] text-white/45 mt-0.5 leading-relaxed">{p.summary}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-white/30 italic">No TwinFlō-elevated priorities this run.</p>
          )}
        </div>

        {/* Contradictions */}
        <div>
          <p className="text-[10px] uppercase tracking-[0.1em] text-white/40 mb-1.5">TwinFlō-driven contradictions</p>
          {tContradictions.length ? (
            <div className="space-y-1" data-testid="twinflo-contradictions">
              {tContradictions.map(c => (
                <div key={c.key} className="border-l-2 border-rose-400/40 pl-3 py-0.5">
                  <p className="text-xs text-white/85">{c.label}</p>
                  <p className="text-[11px] text-white/45 mt-0.5 leading-relaxed">{c.summary}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-white/30 italic">No labs-vs-state mismatches detected.</p>
          )}
        </div>

        {/* Trajectory effect */}
        <div className="flex items-center justify-between pt-1 border-t border-white/[0.04]">
          <span className="text-[10px] uppercase tracking-[0.1em] text-white/40">Trajectory effect</span>
          <span
            className={`text-[11px] ${trajectoryAdjusted ? 'text-[#C89B7B]' : 'text-white/40'}`}
            data-testid="twinflo-trajectory-effect"
          >
            {trajectoryAdjusted ? 'Adjusted by TwinFlō' : 'No adjustment'}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────── Execution Influence Panel ────────────────────────
// Read-only visibility layer showing how execution (adherence) reality
// influenced this DECODE run. Reads from intelligence.module_outputs.execution
// and intelligence.module_outputs.decode.execution_influence.

const EXECUTION_PATTERN_LABELS = {
  stable_adherence:            'Stable Adherence',
  improving_execution:         'Improving Execution',
  execution_drop:              'Recent Execution Drop',
  partial_adherence:           'Partial Adherence',
  fragile_execution:           'Fragile / Volatile Execution',
  medication_inconsistency:    'Medication Inconsistency',
  food_protocol_inconsistency: 'Food-Protocol Inconsistency',
  therapy_inconsistency:       'Therapy Inconsistency',
  nonadherence_cluster:        'Nonadherence Cluster',
  high_execution_burden:       'High Execution Burden',
  timing_instability:          'Timing Instability',
  recovery_support_low:        'Recovery Support Underused',
};

const EXECUTION_PATTERN_CATEGORY = {
  stable_adherence: 'optimal',
  improving_execution: 'optimal',
  nonadherence_cluster: 'critical',
  execution_drop: 'critical',
  medication_inconsistency: 'critical',
  fragile_execution: 'strain',
  food_protocol_inconsistency: 'strain',
  therapy_inconsistency: 'strain',
  timing_instability: 'strain',
  partial_adherence: 'strain',
  high_execution_burden: 'strain',
  recovery_support_low: 'strain',
};

function ExecutionInfluencePanel({ intelligence }) {
  const ex = intelligence?.module_outputs?.execution || {};
  const infl = intelligence?.module_outputs?.decode?.execution_influence || {};
  const integrated = !!intelligence?.meta?.execution_integrated;

  // Absent: no execution data at all
  if (!integrated && !ex.present && !ex.is_stale) {
    return (
      <Card
        className="bg-white/[0.02] border-dashed border-white/10"
        data-testid="decode-execution-panel-absent"
      >
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] text-white/35">
            <CheckCircle2 className="w-3.5 h-3.5" /> Execution Influence
          </div>
          <p className="text-[11px] text-white/40 italic mt-2">
            No meaningful execution history yet — DECODE is using lab + state only.
            Protocol implementation will start feeding the backbone after a few logged days.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Stale
  if (ex.is_stale) {
    return (
      <Card
        className="bg-amber-950/10 border-amber-400/20"
        data-testid="decode-execution-panel-stale"
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] text-amber-200/70">
              <CheckCircle2 className="w-3.5 h-3.5" /> Execution Influence
            </div>
            <Badge className="text-[9px] bg-amber-400/15 text-amber-200 rounded-sm border-0">stale</Badge>
          </div>
          <p className="text-[11px] text-amber-200/70 mt-2 leading-relaxed">
            Last execution entry is {ex.days_since ?? '>14'} days old — excluded from weighting.
            Biology + state drive this DECODE alone.
          </p>
        </CardContent>
      </Card>
    );
  }

  const dom = ex.dominant_pattern;
  const domLabel = EXECUTION_PATTERN_LABELS[dom] || dom?.replace?.(/_/g, ' ') || '—';
  const domCategory = EXECUTION_PATTERN_CATEGORY[dom] || 'strain';
  const w7 = ex.windows?.['7d'] || {};
  const w14 = ex.windows?.['14d'] || {};
  const w3 = ex.windows?.['3d'] || {};
  const adh7 = w7.adherence;
  const adh14 = w14.adherence;
  const adh3 = w3.adherence;
  const timing = w7.timing_consistency;
  const volatility = w7.volatility;
  const categories = ex.by_category || {};

  const exPriorities = ex.execution_priorities || [];
  const exContradictions = ex.execution_contradictions || [];
  const trajectoryAdjusted = !!infl.trajectory_adjusted;

  const freshBadge = ex.is_fresh
    ? { label: 'fresh', cls: 'bg-emerald-400/15 text-emerald-200' }
    : { label: `${ex.days_since ?? '—'}d old`, cls: 'bg-white/10 text-white/60' };
  const categoryCls =
    domCategory === 'optimal' ? 'bg-emerald-400/15 text-emerald-200'
      : domCategory === 'critical' ? 'bg-rose-400/15 text-rose-200'
        : 'bg-amber-400/15 text-amber-200';

  return (
    <Card
      className="border-[1px] border-emerald-400/20 bg-gradient-to-br from-emerald-400/[0.04] to-white/[0.02]"
      data-testid="decode-execution-panel"
    >
      <CardContent className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] text-emerald-200/90">
            <CheckCircle2 className="w-3.5 h-3.5" /> Execution Influence
          </div>
          <div className="flex items-center gap-1.5">
            <Badge className={`text-[9px] rounded-sm border-0 ${freshBadge.cls}`}>{freshBadge.label}</Badge>
            <Badge className={`text-[9px] rounded-sm border-0 ${categoryCls}`}>{domCategory}</Badge>
          </div>
        </div>

        <p className="text-[11px] text-white/55 leading-relaxed italic">
          Protocol implementation reality — <span className="text-white/70">contextualizes</span> biological interpretation, never replaces it.
        </p>

        {/* State strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-1">
          <StatTile
            label="Dominant pattern"
            value={domLabel}
            sub={`${ex.days_logged || 0} day(s) logged`}
            testid="execution-dominant"
          />
          <StatTile
            label="7-day adherence"
            value={adh7 != null ? `${Math.round(adh7 * 100)}%` : '—'}
            sub={adh3 != null ? `3d ${Math.round(adh3 * 100)}% · 14d ${adh14 != null ? Math.round(adh14 * 100) + '%' : '—'}` : null}
            testid="execution-adherence"
          />
          <StatTile
            label="Consistency"
            value={volatility != null ? `vol ${Number(volatility).toFixed(2)}` : '—'}
            sub={timing != null ? `timing ${Math.round(timing * 100)}% on-time` : null}
            testid="execution-consistency"
          />
          <StatTile
            label="Last entry"
            value={ex.last_date || '—'}
            sub={ex.is_fresh ? 'fresh' : `${ex.days_since ?? '—'}d ago`}
            testid="execution-last-entry"
          />
        </div>

        {/* Category breakdown */}
        {Object.keys(categories).length > 0 && (
          <div className="pt-1">
            <p className="text-[10px] uppercase tracking-[0.1em] text-white/40 mb-1.5">By category (14d)</p>
            <div className="flex flex-wrap gap-1.5" data-testid="execution-by-category">
              {Object.entries(categories).slice(0, 6).map(([cat, m]) => {
                const pct = m.total ? Math.round((m.confirmed / m.total) * 100) : 0;
                const tone = pct >= 80 ? 'text-emerald-200 border-emerald-400/25 bg-emerald-400/10'
                  : pct >= 50 ? 'text-amber-200 border-amber-400/25 bg-amber-400/10'
                    : 'text-rose-200 border-rose-400/25 bg-rose-400/10';
                return (
                  <Badge
                    key={cat}
                    className={`text-[10px] border rounded-sm ${tone}`}
                    title={`${m.confirmed}/${m.total} confirmed (missed ${m.missed || 0}, skipped ${m.skipped || 0})`}
                  >
                    {cat} {pct}%
                  </Badge>
                );
              })}
            </div>
          </div>
        )}

        {/* Priorities */}
        <div>
          <p className="text-[10px] uppercase tracking-[0.1em] text-white/40 mb-1.5">Priorities from Execution</p>
          {exPriorities.length ? (
            <div className="space-y-1" data-testid="execution-priorities">
              {exPriorities.map((p, i) => (
                <div key={`${p.source_key || p.label}-${i}`} className="border-l-2 border-emerald-400/40 pl-3 py-0.5">
                  <div className="flex items-center gap-2 text-xs">
                    <Badge className="text-[8px] rounded-sm bg-emerald-400/20 text-emerald-200 border-0">
                      {p.type || 'signal'}
                    </Badge>
                    <span className="text-white/80">{p.label}</span>
                    {p.severity && <span className="text-white/30 ml-auto text-[10px]">{p.severity}</span>}
                  </div>
                  {p.summary && (
                    <p className="text-[11px] text-white/45 mt-0.5 leading-relaxed">{p.summary}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-white/30 italic">No execution-elevated priorities this run.</p>
          )}
        </div>

        {/* Contradictions */}
        <div>
          <p className="text-[10px] uppercase tracking-[0.1em] text-white/40 mb-1.5">Execution-driven contradictions</p>
          {exContradictions.length ? (
            <div className="space-y-1" data-testid="execution-contradictions">
              {exContradictions.map(c => (
                <div key={c.key} className="border-l-2 border-rose-400/40 pl-3 py-0.5">
                  <p className="text-xs text-white/85">{c.label}</p>
                  <p className="text-[11px] text-white/45 mt-0.5 leading-relaxed">{c.summary}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-white/30 italic">No biology-vs-execution mismatches detected.</p>
          )}
        </div>

        {/* Trajectory effect */}
        <div className="flex items-center justify-between pt-1 border-t border-white/[0.04]">
          <span className="text-[10px] uppercase tracking-[0.1em] text-white/40">Trajectory effect</span>
          <span
            className={`text-[11px] ${trajectoryAdjusted ? 'text-emerald-200' : 'text-white/40'}`}
            data-testid="execution-trajectory-effect"
          >
            {trajectoryAdjusted ? 'Adjusted by Execution' : 'No adjustment'}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────── Passive Signals Panel ────────────────────────
// Read-only visibility layer for Wearables + Sleep + Nutrition backbone
// influence. Parallel to TwinFlō Influence and Execution Influence.

const WEARABLE_PATTERN_LABELS = {
  stable_physiology:    'Stable Physiology',
  hrv_drop:             'HRV Drop',
  rhr_elevation:        'Resting HR Elevation',
  low_recovery_cluster: 'Low Recovery Cluster',
  high_strain_cluster:  'High Strain Cluster',
  activity_deficit:     'Activity Deficit',
  improving_physiology: 'Improving Physiology',
  fragile_physiology:   'Volatile Physiology',
};
const SLEEP_PATTERN_LABELS = {
  stable_sleep:            'Stable Sleep',
  short_sleep_cluster:     'Short-Sleep Cluster',
  fragmented_sleep:        'Fragmented Sleep',
  low_deep_sleep:          'Low Deep Sleep',
  improving_sleep:         'Improving Sleep',
  sleep_deprivation:       'Sleep Deprivation',
  unstable_recovery_sleep: 'Unstable Recovery Sleep',
  poor_sleep_consistency:  'Poor Sleep Consistency',
};
const NUTRITION_PATTERN_LABELS = {
  stable_nutrition:        'Stable Nutrition',
  low_nutrition_adherence: 'Low Nutrition Adherence',
  missing_meals_cluster:   'Missing Meals Cluster',
  improving_nutrition:     'Improving Nutrition',
  fragile_nutrition:       'Volatile Nutrition',
};
const PASSIVE_WEAK_PATTERNS = new Set([
  'hrv_drop', 'rhr_elevation', 'low_recovery_cluster', 'high_strain_cluster',
  'short_sleep_cluster', 'fragmented_sleep', 'low_deep_sleep', 'sleep_deprivation',
  'poor_sleep_consistency', 'unstable_recovery_sleep',
  'low_nutrition_adherence', 'missing_meals_cluster', 'fragile_nutrition',
]);
const PASSIVE_STRONG_PATTERNS = new Set([
  'stable_physiology', 'improving_physiology',
  'stable_sleep', 'improving_sleep',
  'stable_nutrition', 'improving_nutrition',
]);

function PassiveSignalsPanel({ intelligence }) {
  const wearable = intelligence?.module_outputs?.wearables || {};
  const sleep = intelligence?.module_outputs?.sleep || {};
  const nutrition = intelligence?.module_outputs?.nutrition || {};
  const infl = intelligence?.module_outputs?.decode?.passive_influence || {};
  const meta = intelligence?.meta || {};
  const anyIntegrated =
    !!meta.wearable_integrated || !!meta.sleep_integrated || !!meta.nutrition_integrated;
  const anyStale = wearable.is_stale || sleep.is_stale || nutrition.is_stale;

  // Absent
  if (!anyIntegrated && !anyStale) {
    return (
      <Card
        className="bg-white/[0.02] border-dashed border-white/10"
        data-testid="decode-passive-panel-absent"
      >
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] text-white/35">
            <Leaf className="w-3.5 h-3.5" /> Passive Signals Influence
          </div>
          <p className="text-[11px] text-white/40 italic mt-2">
            No wearable, sleep, or nutrition signal available — DECODE is using lab + state + execution only.
            Passive signal families will start feeding the backbone once data exists.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Stale-only
  if (!anyIntegrated && anyStale) {
    const which = [
      wearable.is_stale && 'wearable', sleep.is_stale && 'sleep', nutrition.is_stale && 'nutrition',
    ].filter(Boolean).join(', ');
    return (
      <Card
        className="bg-amber-950/10 border-amber-400/20"
        data-testid="decode-passive-panel-stale"
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] text-amber-200/70">
              <Leaf className="w-3.5 h-3.5" /> Passive Signals Influence
            </div>
            <Badge className="text-[9px] bg-amber-400/15 text-amber-200 rounded-sm border-0">stale</Badge>
          </div>
          <p className="text-[11px] text-amber-200/70 mt-2 leading-relaxed">
            Passive signals present ({which}) but stale — excluded from weighting. Lab + state + execution drive this DECODE alone.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Present
  const tPriorities = intelligence?.priorities?.filter(p =>
    ['wearable', 'sleep', 'nutrition'].includes(p.source_type)
  ) || [];
  const tContradictions = (infl.contradictions_added || []).map(key => {
    const c = (intelligence?.contradictions || []).find(x => x.key === key);
    return c ? { key, label: c.label, summary: c.summary } : null;
  }).filter(Boolean);
  const trajectoryAdjusted = !!infl.trajectory_adjusted;

  // Freshness aggregate: oldest freshness among present families
  const daysList = [wearable, sleep, nutrition]
    .filter(b => b.present)
    .map(b => b.days_since)
    .filter(v => v != null);
  const maxDays = daysList.length ? Math.max(...daysList) : null;
  const allFresh = [wearable, sleep, nutrition].filter(b => b.present).every(b => b.is_fresh);
  const freshBadge = allFresh
    ? { label: 'fresh', cls: 'bg-emerald-400/15 text-emerald-200' }
    : { label: `${maxDays ?? '—'}d old`, cls: 'bg-white/10 text-white/60' };

  // Overall tone from the families present
  const presentFamilies = [wearable, sleep, nutrition].filter(b => b.present);
  const weakCount = presentFamilies.filter(b => PASSIVE_WEAK_PATTERNS.has(b.dominant_pattern)).length;
  const strongCount = presentFamilies.filter(b => PASSIVE_STRONG_PATTERNS.has(b.dominant_pattern)).length;
  const tone = weakCount >= 2 ? 'critical'
    : weakCount >= 1 ? 'strain'
      : strongCount >= 1 ? 'optimal' : 'neutral';
  const toneCls =
    tone === 'optimal' ? 'bg-emerald-400/15 text-emerald-200'
      : tone === 'critical' ? 'bg-rose-400/15 text-rose-200'
        : tone === 'strain' ? 'bg-amber-400/15 text-amber-200'
          : 'bg-white/10 text-white/60';

  return (
    <Card
      className="border-[1px] border-sky-400/20 bg-gradient-to-br from-sky-400/[0.04] to-white/[0.02]"
      data-testid="decode-passive-panel"
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] text-sky-200/90">
            <Leaf className="w-3.5 h-3.5" /> Passive Signals Influence
          </div>
          <div className="flex items-center gap-1.5">
            <Badge className={`text-[9px] rounded-sm border-0 ${freshBadge.cls}`}>{freshBadge.label}</Badge>
            <Badge className={`text-[9px] rounded-sm border-0 ${toneCls}`}>{tone}</Badge>
          </div>
        </div>

        <p className="text-[11px] text-white/55 leading-relaxed italic">
          Wearables · Sleep · Nutrition — <span className="text-white/70">tertiary contextual refiners</span>; never overrides biology.
        </p>

        {/* Family row: each family gets a compact tile */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <FamilyTile
            testid="passive-family-sleep"
            name="Sleep"
            block={sleep}
            labels={SLEEP_PATTERN_LABELS}
            subFormatter={(b) => {
              const m = b.metrics || {};
              const dur = m.sleep_duration || {};
              const short = m.short_nights_7d;
              const bits = [];
              if (dur.recent_3d != null) bits.push(`${Number(dur.recent_3d).toFixed(1)}h (3d)`);
              if (short) bits.push(`${short} short / 7d`);
              return bits.join(' · ');
            }}
          />
          <FamilyTile
            testid="passive-family-wearable"
            name="Wearable"
            block={wearable}
            labels={WEARABLE_PATTERN_LABELS}
            subFormatter={(b) => {
              const m = b.metrics || {};
              const hrv = m.hrv || {};
              const rec = m.recovery_score || {};
              const bits = [];
              if (hrv.recent_3d != null) bits.push(`HRV ${Math.round(hrv.recent_3d)}`);
              if (rec.recent_3d != null) bits.push(`rec ${Math.round(rec.recent_3d)}`);
              return bits.join(' · ');
            }}
          />
          <FamilyTile
            testid="passive-family-nutrition"
            name="Nutrition"
            block={nutrition}
            labels={NUTRITION_PATTERN_LABELS}
            subFormatter={(b) => {
              const m = b.metrics || {};
              if (m.adherence_14d != null) return `${Math.round(m.adherence_14d * 100)}% 14d adherence`;
              return null;
            }}
          />
        </div>

        {/* Priorities */}
        <div>
          <p className="text-[10px] uppercase tracking-[0.1em] text-white/40 mb-1.5">Priorities from Passive Signals</p>
          {tPriorities.length ? (
            <div className="space-y-1" data-testid="passive-priorities">
              {tPriorities.map((p, i) => (
                <div key={`${p.source_key || p.label}-${i}`} className="border-l-2 border-sky-400/40 pl-3 py-0.5">
                  <div className="flex items-center gap-2 text-xs">
                    <Badge className="text-[8px] rounded-sm bg-sky-400/20 text-sky-200 border-0">{p.source_type}</Badge>
                    <span className="text-white/80">{p.label}</span>
                    {p.severity && <span className="text-white/30 ml-auto text-[10px]">{p.severity}</span>}
                  </div>
                  {p.summary && <p className="text-[11px] text-white/45 mt-0.5 leading-relaxed">{p.summary}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-white/30 italic">No passive-elevated priorities this run.</p>
          )}
        </div>

        {/* Contradictions */}
        <div>
          <p className="text-[10px] uppercase tracking-[0.1em] text-white/40 mb-1.5">Passive-driven contradictions</p>
          {tContradictions.length ? (
            <div className="space-y-1" data-testid="passive-contradictions">
              {tContradictions.map(c => (
                <div key={c.key} className="border-l-2 border-rose-400/40 pl-3 py-0.5">
                  <p className="text-xs text-white/85">{c.label}</p>
                  <p className="text-[11px] text-white/45 mt-0.5 leading-relaxed">{c.summary}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-white/30 italic">No passive-driven mismatches detected.</p>
          )}
        </div>

        {/* Trajectory effect */}
        <div className="flex items-center justify-between pt-1 border-t border-white/[0.04]">
          <span className="text-[10px] uppercase tracking-[0.1em] text-white/40">Trajectory effect</span>
          <span
            className={`text-[11px] ${trajectoryAdjusted ? 'text-sky-200' : 'text-white/40'}`}
            data-testid="passive-trajectory-effect"
          >
            {trajectoryAdjusted ? 'Adjusted by Passive Signals' : 'No adjustment'}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function FamilyTile({ name, block, labels, subFormatter, testid }) {
  const present = block?.present;
  const dom = block?.dominant_pattern;
  const label = dom ? (labels[dom] || dom.replace(/_/g, ' ')) : '—';
  const sub = present ? (subFormatter?.(block) || null) : null;
  const weak = present && PASSIVE_WEAK_PATTERNS.has(dom);
  const strong = present && PASSIVE_STRONG_PATTERNS.has(dom);
  const tone = !present ? 'text-white/30'
    : weak ? 'text-rose-200'
      : strong ? 'text-emerald-200'
        : 'text-amber-200';
  const border = !present ? 'border-white/[0.06]'
    : weak ? 'border-rose-400/25'
      : strong ? 'border-emerald-400/20'
        : 'border-amber-400/20';

  return (
    <div className={`rounded-sm border ${border} bg-white/[0.02] px-3 py-2`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.1em] text-white/40">{name}</p>
        {present && (
          <Badge className="text-[8px] rounded-sm bg-white/5 text-white/60 border-0">
            {block.is_fresh ? 'fresh' : `${block.days_since ?? '—'}d`}
          </Badge>
        )}
      </div>
      <p className={`text-xs mt-0.5 ${tone}`}>{present ? label : 'no data'}</p>
      {sub && <p className="text-[10px] text-white/35 mt-0.5">{sub}</p>}
    </div>
  );
}

const STATE_LABELS = {
  calm_alignment: 'Calm Alignment',
  nervous_system_strain: 'Nervous System Strain',
  cognitive_overload: 'Cognitive Overload',
  anxiety_loop: 'Anxiety Loop',
  fragmented_focus: 'Fragmented Focus',
  recovery_debt: 'Recovery Debt',
  emotional_fatigue: 'Emotional Fatigue',
  irritable_reactivity: 'Irritable Reactivity',
  performative_flow: 'Performative Flow',
};

function labelForState(key) {
  if (!key) return '—';
  return STATE_LABELS[key] || key.replace(/_/g, ' ');
}

function StatTile({ label, value, sub, testid }) {
  return (
    <div className="rounded-sm bg-white/[0.02] border border-white/[0.06] px-2.5 py-1.5" data-testid={testid}>
      <p className="text-[9px] uppercase tracking-[0.1em] text-white/40">{label}</p>
      <p className="text-xs text-white/85 mt-0.5 truncate">{value || '—'}</p>
      {sub && <p className="text-[10px] text-white/35 mt-0.5 truncate">{sub}</p>}
    </div>
  );
}

const EmptyLine = ({ text }) => <p className="text-[11px] text-white/30 italic">{text}</p>;

const EmptyState = ({ msg }) => (
  <div className="border border-white/5 rounded-sm bg-white/[0.02] p-10 text-center text-white/40 text-xs">{msg}</div>
);

const StatusDot = ({ status }) => {
  const color = status === 'critical' ? 'bg-red-400'
    : status === 'stressed' ? 'bg-amber-400'
    : status === 'suboptimal' ? 'bg-blue-400'
    : 'bg-emerald-400';
  return <span className={`inline-block w-1.5 h-1.5 rounded-full ${color}`} />;
};

const PriorityRow = ({ p, tone }) => {
  const typeColor = p.type === 'root_driver' ? 'bg-red-800/30 text-red-300'
    : p.type === 'amplifier' ? 'bg-amber-800/30 text-amber-300'
    : p.type === 'secondary' ? 'bg-blue-800/30 text-blue-300'
    : 'bg-white/10 text-white/50';
  const borderClass = tone === 'red' ? 'border-red-400/40' : tone === 'amber' ? 'border-amber-400/40' : 'border-white/10';
  return (
    <div className={`border-l-2 ${borderClass} pl-3 py-1.5`}>
      <div className="flex items-center gap-2 text-xs">
        <span className="text-white/30 tabular-nums">#{p.rank}</span>
        <Badge className={`text-[8px] rounded-sm ${typeColor}`}>{p.type}</Badge>
        <span className="text-white/85">{p.label}</span>
        <span className="text-white/30 ml-auto text-[10px]">{p.severity}</span>
      </div>
      {p.recommended_focus && (
        <p className="text-[11px] text-white/45 mt-0.5 leading-relaxed">→ {p.recommended_focus}</p>
      )}
    </div>
  );
};

function EditableField({ label, value, onChange, rows = 3, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.1em] text-white/50 mb-1.5">{label}</label>
      <Textarea
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        rows={rows}
        data-testid={testid}
        className="bg-white/[0.02] border-white/10 text-white text-xs resize-none focus:border-[#C89B7B]"
      />
    </div>
  );
}

function EditableListField({ label, value, onChange, rows = 3, testid }) {
  const text = Array.isArray(value) ? value.join('\n') : (value || '');
  return (
    <EditableField
      label={label}
      rows={rows}
      testid={testid}
      value={text}
      onChange={t => onChange(t.split('\n').map(s => s.trim()).filter(Boolean))}
    />
  );
}

// ──────────────────────── mapping helpers ────────────────────────

function mapDraftToForm(draft) {
  return {
    strategic_direction: draft.strategic_direction || '',
    current_state: draft.current_state || '',
    current_modifiable_window: draft.current_modifiable_window || '',
    bas_summary: draft.bas_summary || '',
    nas_summary: draft.nas_summary || '',
    tas_summary: draft.tas_summary || '',
    neuron_status: draft.neuron_status || '',
    mitochondrial_status: draft.mitochondrial_status || '',
    inflammation_status: draft.inflammation_status || '',
    buffering_status: draft.buffering_status || '',
    gut_status: draft.gut_status || '',
    detox_load: draft.detox_load || '',
    top_drivers: draft.top_drivers || [],
    phase_objectives: draft.phase_objectives || [],
    subscriber_facing_summary: draft.subscriber_facing_summary || '',
    internal_technical_summary: draft.internal_technical_summary || '',
    detailed_data: draft.detailed_data || null,
    linked_lab_report_ids: draft.linked_lab_report_ids || [],
    linked_intake_form_ids: draft.linked_intake_form_ids || [],
    linked_video_assessment_ids: draft.linked_video_assessment_ids || [],
  };
}

function toApiPayload(draft, intelligence, runId) {
  const detailed = draft.detailed_data || {};
  if (intelligence) {
    detailed.engine_version = intelligence.meta?.engine_version || detailed.engine_version;
    detailed.overall_confidence = intelligence.meta?.overall_confidence || detailed.overall_confidence;
    detailed.axis_analysis = intelligence.axis_analysis || detailed.axis_analysis;
    detailed.priorities = intelligence.priorities || detailed.priorities;
    detailed.contradictions = intelligence.contradictions || detailed.contradictions;
    detailed.relationship_signals = intelligence.relationship_signals || detailed.relationship_signals;
    detailed.trajectory = intelligence.trajectory || detailed.trajectory;
    detailed.intelligence_input_summary = intelligence.input_summary || detailed.intelligence_input_summary;
    detailed.module_outputs = intelligence.module_outputs || detailed.module_outputs;
    detailed.twinflo_integrated = intelligence.meta?.twinflo_integrated ?? detailed.twinflo_integrated;
    if (runId) detailed.source_intelligence_run_id = runId;
  }
  return { ...draft, detailed_data: detailed };
}


// ─── Pre-publish quality gate: deterministic engine-artifact scan ───

// Fields that are visible to subscribers on /decode — never internal-only.
const SUBSCRIBER_FACING_FIELDS = [
  { key: 'strategic_direction', label: 'Strategic Direction' },
  { key: 'current_state', label: 'Current State' },
  { key: 'current_modifiable_window', label: 'Current Modifiable Window' },
  { key: 'bas_summary', label: 'BAS Summary' },
  { key: 'nas_summary', label: 'NAS Summary' },
  { key: 'tas_summary', label: 'TAS Summary' },
  { key: 'subscriber_facing_summary', label: 'Subscriber-Facing Summary' },
];

// Each pattern: regex + short label + severity (warn | strong_warn).
// Kept conservative — false positives are annoying but not dangerous here.
const ARTIFACT_PATTERNS = [
  { key: 'raw_score', re: /\bscore\s+[01](\.\d+)?\b/i, label: 'raw engine score (e.g. "score 0.72")', severity: 'strong_warn' },
  { key: 'supporting_markers', re: /\bsupporting\s+markers?\b/i, label: 'phrase "supporting markers"', severity: 'strong_warn' },
  { key: 'axis_key_snake', re: /[a-z]+_(axis|analysis|status|engine)\b/, label: 'engine key (snake_case)', severity: 'strong_warn' },
  { key: 'root_driver_term', re: /\broot[_\s]driver\b/i, label: 'engine term "root_driver"', severity: 'warn' },
  { key: 'amplifier_term', re: /\bamplifier\b/i, label: 'engine term "amplifier"', severity: 'warn' },
  { key: 'module_outputs', re: /\bmodule_outputs\b/i, label: 'engine term "module_outputs"', severity: 'strong_warn' },
  { key: 'engine_version', re: /regenflo-intel-backbone|engine_version/i, label: 'engine version string', severity: 'strong_warn' },
  { key: 'confidence_label', re: /\b(confidence|overall_confidence)\s*[:=]/i, label: 'internal confidence tag', severity: 'warn' },
  { key: 'insufficient_data_raw', re: /insufficient_data/i, label: 'raw status code "insufficient_data"', severity: 'strong_warn' },
  { key: 'json_fragment', re: /[{[].*[:,].*[}\]]/, label: 'JSON-like fragment', severity: 'strong_warn' },
  { key: 'stressed_raw', re: /\b(critical|stressed|suboptimal|optimal)\s*\(score\b/i, label: 'combined status+score phrase', severity: 'strong_warn' },
];

function scanForEngineArtifacts(draft) {
  const issues = [];
  for (const f of SUBSCRIBER_FACING_FIELDS) {
    const value = draft?.[f.key];
    if (!value || typeof value !== 'string') continue;
    const matches = [];
    for (const p of ARTIFACT_PATTERNS) {
      const m = value.match(p.re);
      if (m) matches.push({ pattern: p.label, severity: p.severity, snippet: extractSnippet(value, m.index, m[0].length) });
    }
    if (matches.length > 0) issues.push({ field_key: f.key, field_label: f.label, matches });
  }
  return issues;
}

function extractSnippet(text, idx, matchLen) {
  const start = Math.max(0, idx - 25);
  const end = Math.min(text.length, idx + matchLen + 25);
  const pre = start > 0 ? '…' : '';
  const post = end < text.length ? '…' : '';
  return pre + text.slice(start, end).replace(/\s+/g, ' ') + post;
}

function ArtifactWarningDialog({ issues, onReview, onPublishAnyway, onCancel }) {
  const strongCount = issues.reduce((n, i) => n + i.matches.filter(m => m.severity === 'strong_warn').length, 0);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm"
      data-testid="artifact-warning-dialog"
    >
      <div className="max-w-2xl w-full bg-[#0b0e14] border border-white/10 rounded-sm shadow-2xl">
        <div className="p-5 border-b border-white/10 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="text-white text-base font-medium">Possible engine wording detected</h2>
            <p className="text-white/50 text-xs mt-1 leading-relaxed">
              {issues.length} subscriber-facing field{issues.length > 1 ? 's' : ''} may still contain internal engine phrasing.
              {strongCount > 0 && <> {strongCount} stronger match{strongCount > 1 ? 'es' : ''}.</>} Review before publishing so the subscriber sees clean, plain language.
            </p>
          </div>
        </div>

        <div className="p-5 max-h-[60vh] overflow-auto space-y-4">
          {issues.map(iss => (
            <div key={iss.field_key} className="border border-white/[0.06] rounded-sm p-3 bg-white/[0.02]" data-testid={`artifact-issue-${iss.field_key}`}>
              <div className="text-[11px] uppercase tracking-[0.12em] text-[#C89B7B]">{iss.field_label}</div>
              <ul className="mt-2 space-y-2">
                {iss.matches.map((m, i) => (
                  <li key={i} className="text-[11px] leading-relaxed">
                    <span className={`inline-block px-1.5 py-0.5 rounded-sm mr-2 text-[9px] ${m.severity === 'strong_warn' ? 'bg-red-900/30 text-red-300' : 'bg-amber-900/30 text-amber-300'}`}>
                      {m.severity === 'strong_warn' ? 'strong' : 'warn'}
                    </span>
                    <span className="text-white/70">{m.pattern}</span>
                    <div className="text-white/50 italic mt-0.5 ml-1 font-mono text-[10px]">"{m.snippet}"</div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-white/10 flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            onClick={onCancel}
            className="h-9 px-4 text-xs text-white/60 hover:bg-white/5"
            data-testid="artifact-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={onReview}
            className="h-9 px-4 text-xs border-white/15 text-white/80"
            data-testid="artifact-review-btn"
          >
            Review before publish
          </Button>
          <Button
            onClick={onPublishAnyway}
            className="h-9 px-4 text-xs bg-[#C89B7B] hover:bg-[#B88A6A] text-[#080C14]"
            data-testid="artifact-publish-anyway-btn"
          >
            Publish anyway
          </Button>
        </div>
      </div>
    </div>
  );
}


// ─── Biological Context Memory Influence Panel ────────────────────────
// Read-only compact view of how Biological Context 2.0 memory is influencing
// this DECODE run. Reads module_outputs.biocontext from the intelligence run.
function BiocontextMemoryInfluencePanel({ intelligence }) {
  const bc = intelligence?.module_outputs?.biocontext || {};
  if (!bc || Object.keys(bc).length === 0) return null;

  const freshness = bc.context_freshness || 'unknown';
  const fresh_color = {
    new: '#64748b', current: '#10b981', partly_stale: '#D4A843',
    stale: '#f43f5e', needs_review: '#f43f5e', unknown: '#64748b',
  }[freshness];
  const acr = bc.active_case_reading_snapshot || {};
  const acr_used = bc.active_case_reading_used;
  const accepted = bc.accepted_system_suggestions || [];
  const stale = bc.stale_sections || [];

  return (
    <Panel title="Biological Context Memory Influence" icon={Activity} testid="decode-biocontext-memory-panel">
      <div className="space-y-3">
        <div className="flex flex-wrap gap-3 items-center text-[10.5px] font-['Work_Sans']">
          <div className="flex items-center gap-1.5">
            <span className="text-white/40">Freshness:</span>
            <span className="px-2 py-0.5 rounded-full" style={{ backgroundColor: `${fresh_color}20`, color: fresh_color }}>
              {freshness.replace('_', ' ')}
            </span>
          </div>
          {bc.completeness_score !== null && bc.completeness_score !== undefined && (
            <div className="flex items-center gap-1.5">
              <span className="text-white/40">Completeness:</span>
              <span className="text-white/70 font-mono">{bc.completeness_score}%</span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <span className="text-white/40">Source:</span>
            <span className="text-white/70">
              {bc.source === 'biocontext_memory_2_0' ? 'Memory 2.0 (canonical)' : 'Legacy V2 / V1'}
            </span>
          </div>
          {bc.parkinson_overlay_used && (
            <span className="px-2 py-0.5 rounded-full bg-[#a78bfa]/20 text-[#a78bfa]">
              Parkinson overlay active
            </span>
          )}
        </div>

        {acr_used && (
          <div className="rounded-lg p-3 bg-white/[0.02] border border-white/[0.05]" data-testid="decode-acr-snapshot">
            <p className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-2 font-['Work_Sans']">
              Active case reading
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px]">
              {acr.current_dominant_driver && (
                <Line label="Dominant driver" value={acr.current_dominant_driver} color="#f43f5e" />
              )}
              {acr.current_amplifier && (
                <Line label="Amplifier" value={acr.current_amplifier} color="#f59e0b" />
              )}
              {acr.current_contradiction && (
                <Line label="Contradiction" value={acr.current_contradiction} color="#a78bfa" />
              )}
              {acr.current_life_blocker && (
                <Line label="Life blocker" value={acr.current_life_blocker} color="#64748b" />
              )}
              {acr.current_support_advantage && (
                <Line label="Support advantage" value={acr.current_support_advantage} color="#10b981" />
              )}
            </div>
          </div>
        )}

        {accepted.length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-1.5 font-['Work_Sans']">
              Admin-accepted system signals ({accepted.length})
            </p>
            <div className="space-y-1">
              {accepted.slice(0, 5).map((s, i) => (
                <p key={i} className="text-[11px] text-white/65 font-['Work_Sans']">
                  · <span className="uppercase text-[9.5px] text-white/40">{s.source_type}</span>{' '}
                  {s.affected_sections.length > 0 && (
                    <span className="text-white/45">({s.affected_sections.join(', ')})</span>
                  )}
                </p>
              ))}
            </div>
          </div>
        )}

        {stale.length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-amber-400/70 mb-1 font-['Work_Sans']">
              Stale / empty sections — {stale.length}
            </p>
            <p className="text-[10.5px] text-white/40 font-['Work_Sans']">
              {stale.slice(0, 5).join(', ')}{stale.length > 5 && ` + ${stale.length - 5} more`}
            </p>
          </div>
        )}

        {!acr_used && accepted.length === 0 && stale.length === 0 && (
          <p className="text-[10.5px] text-white/30 italic font-['Work_Sans']">
            No admin memory signals yet. Open Biological Context 2.0 to start shaping this subscriber's canonical memory.
          </p>
        )}
      </div>
    </Panel>
  );
}

function Line({ label, value, color }) {
  return (
    <div className="flex gap-2">
      <span className="w-1 flex-shrink-0 rounded-full" style={{ backgroundColor: color }} />
      <div className="flex-1 min-w-0">
        <p className="text-[9.5px] uppercase tracking-[0.12em] text-white/35 font-['Work_Sans']">{label}</p>
        <p className="text-[11px] text-white/75 font-['Work_Sans']">{value}</p>
      </div>
    </div>
  );
}
